import type {
  FramingBox,
  FramingCoordinateSpace,
  FramingCropPathPoint,
  FramingObservation,
} from "./framingInspector";

export type PixelRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

export function containedMediaRect(
  containerWidth: number,
  containerHeight: number,
  mediaWidth: number,
  mediaHeight: number,
): PixelRect {
  if (
    containerWidth <= 0 ||
    containerHeight <= 0 ||
    mediaWidth <= 0 ||
    mediaHeight <= 0
  ) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }
  const scale = Math.min(
    containerWidth / mediaWidth,
    containerHeight / mediaHeight,
  );
  const width = mediaWidth * scale;
  const height = mediaHeight * scale;
  return {
    x: (containerWidth - width) / 2,
    y: (containerHeight - height) / 2,
    width,
    height,
  };
}

export function projectNormalizedBox(
  box: FramingBox,
  mediaRect: PixelRect,
): PixelRect {
  const xMin = clamp(Math.min(box.xMin, box.xMax), 0, 1);
  const xMax = clamp(Math.max(box.xMin, box.xMax), 0, 1);
  const yMin = clamp(Math.min(box.yMin, box.yMax), 0, 1);
  const yMax = clamp(Math.max(box.yMin, box.yMax), 0, 1);
  return {
    x: mediaRect.x + xMin * mediaRect.width,
    y: mediaRect.y + yMin * mediaRect.height,
    width: (xMax - xMin) * mediaRect.width,
    height: (yMax - yMin) * mediaRect.height,
  };
}

export function cropWindowAtTime(
  path: FramingCropPathPoint[],
  timeSeconds: number,
): FramingBox | null {
  if (path.length === 0) return null;
  if (path.length === 1 || timeSeconds <= path[0].timeSeconds) {
    return path[0].window;
  }
  const last = path.at(-1)!;
  if (timeSeconds >= last.timeSeconds) return last.window;

  const upperIndex = path.findIndex(
    (point) => point.timeSeconds >= timeSeconds,
  );
  const upper = path[upperIndex];
  const lower = path[upperIndex - 1];
  const span = upper.timeSeconds - lower.timeSeconds;
  const amount = span <= 0 ? 0 : (timeSeconds - lower.timeSeconds) / span;
  const interpolate = (start: number, end: number) =>
    start + (end - start) * amount;
  return {
    xMin: interpolate(lower.window.xMin, upper.window.xMin),
    yMin: interpolate(lower.window.yMin, upper.window.yMin),
    xMax: interpolate(lower.window.xMax, upper.window.xMax),
    yMax: interpolate(lower.window.yMax, upper.window.yMax),
  };
}

export function cropPathDuration(path: FramingCropPathPoint[]): number {
  return path.length > 0 ? Math.max(0, path.at(-1)!.timeSeconds) : 0;
}

/**
 * Keep timeless/static observations visible, and select the nearest
 * timestamped detector sample for every subject at the scrubber position.
 *
 * Subject samples are deliberately resolved independently: detectors do not
 * necessarily emit every subject at the same timestamp, so a single global
 * nearest timestamp would make valid subjects blink out of the overlay.
 */
export function observationsAtTime(
  observations: FramingObservation[],
  timeSeconds: number,
): FramingObservation[] {
  const timeless = observations.filter(
    (observation) => observation.timestampSeconds == null,
  );
  const timed = observations.filter(
    (observation) => observation.timestampSeconds != null,
  );
  if (timed.length === 0) return observations;

  const samplesBySubject = new Map<string, FramingObservation[]>();
  for (const observation of timed) {
    const subjectKey = observation.subjectId ?? "__unassigned__";
    const samples = samplesBySubject.get(subjectKey) ?? [];
    samples.push(observation);
    samplesBySubject.set(subjectKey, samples);
  }

  const nearest = [...samplesBySubject.values()].flatMap((samples) => {
    const nearestDistance = Math.min(
      ...samples.map((observation) =>
        Math.abs(observation.timestampSeconds! - timeSeconds),
      ),
    );
    return samples.filter(
      (observation) =>
        Math.abs(
          Math.abs(observation.timestampSeconds! - timeSeconds) -
            nearestDistance,
        ) <=
        1 / 1000,
    );
  });
  return [...timeless, ...nearest];
}

const SWAPPED_ORIENTATIONS = new Set([
  "transpose",
  "transverse",
  "rotate_90_cw",
  "rotate_270_cw",
]);

export function displayDimensionsForSpace(
  coordinateSpace: FramingCoordinateSpace | null,
  coordinateSpaces: FramingCoordinateSpace[],
): { width: number; height: number; orientation: string } | null {
  if (!coordinateSpace?.width || !coordinateSpace.height) return null;
  const outgoingOrientation = coordinateSpaces.find(
    (child) =>
      child.parentSpaceId === coordinateSpace.id &&
      child.transformFromParent?.orientation &&
      child.transformFromParent.orientation !== "normal",
  )?.transformFromParent?.orientation;
  const orientation = outgoingOrientation ?? "normal";
  return SWAPPED_ORIENTATIONS.has(orientation)
    ? {
        width: coordinateSpace.height,
        height: coordinateSpace.width,
        orientation,
      }
    : {
        width: coordinateSpace.width,
        height: coordinateSpace.height,
        orientation,
      };
}

export function intrinsicDimensionsMatch(
  actualWidth: number,
  actualHeight: number,
  expected: { width: number; height: number } | null,
): boolean {
  return (
    expected == null ||
    (actualWidth === expected.width && actualHeight === expected.height)
  );
}
